import { useEffect, useMemo, useRef, useState } from "react";

import MainButton from "~/components/common/mainButton";
import Modal from "~/components/ui/modal";
import TagFilter from "~/components/ui/tagFilter";
import { useLanguage } from "~/contexts/providers/language";
import { useGrabToScroll } from "~/hooks/grabToScroll";
import { PictureType } from "~/types/picture/picture.types";
import { TagType } from "~/types/tag/tag.types";

import CultureImage from "./image";
import styles from "./index.module.scss";
import CultureImagePopupImage from "./popupImage";

interface Props {
  tags: TagType[];
  pictures: PictureType[];
}

const CultureImages = ({ tags, pictures }: Props) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalChildren, setModalChildren] = useState<JSX.Element>(<></>);
  const [filter, setFilter] = useState<TagType[]>([]);

  const containerRef = useRef<HTMLDivElement>(null);
  const { language } = useLanguage();

  useGrabToScroll(containerRef, {
    grabCursor: true,
    removeScrollBar: true,
  });

  const tagIds = useMemo(() => filter.map((tag) => tag.id), [filter]);

  const handleSetFilter = (filter: TagType) => {
    setFilter((prevFilter) => {
      if (prevFilter.includes(filter)) {
        return prevFilter.filter((item) => item !== filter);
      }
      return [...prevFilter, filter];
    });
  };

  const handleResetFilter = () => {
    setFilter([]);
  };

  const handleImageClick = (src: string, alt: string, caption?: string) => {
    setModalChildren(
      <CultureImagePopupImage src={src} alt={alt} caption={caption} />
    );
    setModalOpen((prev) => !prev);
  };

  useEffect(() => {
    const container = containerRef.current;
    if (!!container) {
      container.scroll({
        top: container.scrollHeight / 2,
        left: container.scrollWidth / 2,
      });
    }
  }, [containerRef.current?.scrollHeight, containerRef.current?.scrollWidth]);

  return (
    <section className={styles.cultureImages}>
      <TagFilter
        filter={filter}
        tags={tags}
        relative={false}
        handleSetFilter={handleSetFilter}
        handleResetFilter={handleResetFilter}
        className={styles.filter}
      />
      <MainButton uiOnly className={styles.scrollWidget}>
        ↕ Drag ↔
      </MainButton>
      <div className={styles.container} ref={containerRef}>
        {pictures.map((picture) => (
          <CultureImage
            key={picture.id}
            src={picture.picturePathResized}
            alt={picture.alt}
            caption={picture[`caption${language}`]}
            selected={
              filter.length === 0 ||
              picture.tags
                .map(({ id }) => id)
                .some((tagId) => tagIds.includes(tagId))
            }
            handleImageClick={() =>
              handleImageClick(
                picture.picturePathResized,
                picture.alt ?? "",
                picture[`caption${language}`]
              )
            }
            position={{
              top: picture.top,
              left: picture.left,
              rotation: picture.rotation,
              zIndex: picture.zIndex,
            }}
          />
        ))}
      </div>
      <Modal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
        }}
        showAnimation="expand"
      >
        {modalChildren}
      </Modal>
    </section>
  );
};

export default CultureImages;
